// =============================================================================
// Header navigation: the mobile drawer and the nav dropdowns.
//
// The mockup swaps whole inline style strings from React state. The equivalent
// rules already live in _header.scss behind `.is-open`, so this only toggles
// that class and keeps the aria-expanded flags honest.
//
// There can be more than one dropdown now that the tabs may come from a
// WordPress menu, so every query here is plural and each tab owns its own menu.
//
// A dropdown long enough to be printed with a filter box also narrows itself as
// the shopper types: every row is already in the page, so this only hides the
// ones that do not match.
// =============================================================================

import { claimOverlay, releaseOverlay, onDismiss } from './overlay.js';

// The mockup's isNarrow test. _header.scss breaks at 1023.98px, which is the
// same boundary expressed for CSS.
const NARROW = '(max-width: 1023.98px)';

function isNarrow() {
	return window.matchMedia(NARROW).matches;
}

export default function initNav() {
	const nav = document.getElementById('ch-primary-nav');
	const navToggle = document.querySelector('[data-ch-nav-toggle]');
	const navClose = nav?.querySelector('[data-ch-nav-close]');
	const shopNavs = Array.from(document.querySelectorAll('[data-ch-shop-nav]'));

	// The drawer is a child of the header, whose sticky z-index would otherwise
	// leave it under the overlay. _header.scss lifts the header while this is set.
	const header = nav?.closest('.ch-header');

	let lastFocused = null;

	function setMobileNav(open) {
		if (!nav || !navToggle) {
			return;
		}

		nav.classList.toggle('is-open', open);
		header?.classList.toggle('is-nav-open', open);
		navToggle.setAttribute('aria-expanded', open ? 'true' : 'false');

		if (open) {
			// The drawer dims the page behind it, the way the cart's does. Both
			// go through the same claim so whichever closes last clears it.
			claimOverlay('nav');
			lastFocused = document.activeElement;
			navClose?.focus();
		} else {
			releaseOverlay('nav');
			closeShopMenus();

			// Only pull focus back if it is still inside the panel we just hid,
			// so a click elsewhere on the page is not yanked away.
			if (nav.contains(document.activeElement) && lastFocused instanceof HTMLElement) {
				lastFocused.focus();
			}
		}
	}

	function setShopMenu(shopNav, open) {
		if (!shopNav) {
			return;
		}

		shopNav.classList.toggle('is-open', open);
		shopNav
			.querySelector('[data-ch-shop-toggle]')
			?.setAttribute('aria-expanded', open ? 'true' : 'false');

		// A menu put away forgets what was typed into it, so it opens again on the
		// whole list rather than on the last search.
		if (!open) {
			const filter = shopNav.querySelector('[data-ch-nav-filter]');

			if (filter?.value) {
				filter.value = '';
				filter.dispatchEvent(new Event('input'));
			}

			const list = shopNav.querySelector('[data-ch-nav-list]');

			if (list) {
				list.scrollTop = 0;
			}
		}

		// The dropdown sits over the page without dimming it — the user asked
		// for no overlay. Outside clicks and Escape still close it.
	}

	function closeShopMenus(except) {
		shopNavs.forEach((shopNav) => {
			if (shopNav !== except) {
				setShopMenu(shopNav, false);
			}
		});
	}

	if (navToggle) {
		navToggle.addEventListener('click', () => {
			setMobileNav(!nav.classList.contains('is-open'));
		});
	}

	navClose?.addEventListener('click', () => setMobileNav(false));

	/**
	 * Wire the type-to-filter box of one dropdown, when it has one.
	 *
	 * @param {Element} shopNav The dropdown's wrapper.
	 */
	function initMenuFilter(shopNav) {
		const input = shopNav.querySelector('[data-ch-nav-filter]');
		const list = shopNav.querySelector('[data-ch-nav-list]');

		if (!input || !list) {
			return;
		}

		// "All Products" heads the list without being an aisle, so it is never one
		// of the rows searched through.
		const rows = Array.from(list.querySelectorAll('[data-ch-nav-name]:not([data-ch-nav-pinned])'));
		const none = list.querySelector('[data-ch-nav-none]');

		input.addEventListener('input', () => {
			const needle = input.value.trim().toLowerCase();
			let shown = 0;

			rows.forEach((row) => {
				const match = needle === '' || row.dataset.chNavName.includes(needle);

				row.hidden = !match;

				if (match) {
					shown += 1;
				}
			});

			if (none) {
				none.hidden = shown > 0;
			}

			list.scrollTop = 0;
		});

		input.addEventListener('keydown', (event) => {
			// Enter follows the first aisle still showing — the shortest path for
			// someone who knows the name they want.
			if (event.key === 'Enter') {
				event.preventDefault();
				rows.find((row) => !row.hidden)?.click();

				return;
			}

			// Escape clears the search first; only an empty box lets the keypress
			// through to the handler that closes the menu.
			if (event.key === 'Escape' && input.value !== '') {
				event.stopPropagation();
				input.value = '';
				input.dispatchEvent(new Event('input'));
			}
		});
	}

	shopNavs.forEach((shopNav) => {
		initMenuFilter(shopNav);

		shopNav.querySelector('[data-ch-shop-toggle]')?.addEventListener('click', (event) => {
			event.preventDefault();

			const open = !shopNav.classList.contains('is-open');

			// One at a time: in the drawer these stack vertically, so leaving
			// the others open would push the rest of the menu out of reach.
			closeShopMenus(shopNav);
			setShopMenu(shopNav, open);
		});

		// Hover opens the dropdown on desktop. In the drawer the menu is part of
		// the panel, so hover would fight the tap target — only bind it when the
		// desktop layout is active.
		shopNav.addEventListener('mouseenter', () => {
			if (!isNarrow()) {
				setShopMenu(shopNav, true);
			}
		});
		shopNav.addEventListener('mouseleave', () => {
			// A menu being typed into stays open even when the pointer wanders off
			// it; letting hover close it would swallow the search mid-word.
			if (!isNarrow() && !shopNav.contains(document.activeElement)) {
				setShopMenu(shopNav, false);
			}
		});

		// Which leaves focus to close it: once focus has gone elsewhere and the
		// pointer is not on the menu either, nothing is holding it open.
		shopNav.addEventListener('focusout', () => {
			window.setTimeout(() => {
				if (!isNarrow() && !shopNav.contains(document.activeElement) && !shopNav.matches(':hover')) {
					setShopMenu(shopNav, false);
				}
			}, 0);
		});
	});

	// Clicking anywhere outside a dropdown closes it. The overlay only covers the
	// page for the drawer, so on desktop this is what dismisses one.
	document.addEventListener('click', (event) => {
		const inside =
			event.target instanceof Element ? event.target.closest('[data-ch-shop-nav]') : null;

		closeShopMenus(inside);
	});

	// Escape closes the menus even when no overlay is up to catch a click.
	document.addEventListener('keydown', (event) => {
		if (event.key !== 'Escape') {
			return;
		}

		closeShopMenus();
		setMobileNav(false);
	});

	onDismiss(() => {
		closeShopMenus();
		setMobileNav(false);
	});

	// Crossing the breakpoint with the drawer open would leave the page dimmed
	// behind a nav that the desktop layout has already put back in the header.
	window.matchMedia(NARROW).addEventListener('change', () => {
		if (!isNarrow()) {
			setMobileNav(false);
		}
	});
}
